import { api } from "@/lib/api";

/* Phase A-1 endpoints */
export async function fetchAdminMe() {
  const { data } = await api.get("/admin/me");
  return data?.data;
}
export async function fetchAdminStats() {
  const { data } = await api.get("/admin/stats");
  return data?.data;
}
export async function fetchRecentAudit(limit = 20) {
  const { data } = await api.get(`/admin/audit/recent?limit=${limit}`);
  return data?.data || [];
}

/* Phase A-2 endpoints */
export async function verifyAdminRole() {
  const { data } = await api.post("/admin/auth/verify-role");
  return data?.data;
}
export async function fetchSessions() {
  const { data } = await api.get("/admin/sessions");
  return data?.data || [];
}
export async function revokeSession(id) {
  const { data } = await api.delete(`/admin/sessions/${id}`);
  return data;
}
export async function fetchAuditLogs({ page = 1, limit = 50, action, user_email, ip, status, critical_only } = {}) {
  const params = new URLSearchParams({ page, limit });
  if (action) params.append("action", action);
  if (user_email) params.append("user_email", user_email);
  if (ip) params.append("ip", ip);
  if (status) params.append("status", status);
  if (critical_only) params.append("critical_only", "true");
  const { data } = await api.get(`/admin/audit-logs?${params}`);
  return data;
}
export async function fetchSecurityEvents(only_unresolved = true, limit = 100) {
  const { data } = await api.get(`/admin/security-events?only_unresolved=${only_unresolved}&limit=${limit}`);
  return data?.data || [];
}
export async function resolveSecurityEvent(id) {
  const { data } = await api.patch(`/admin/security-events/${id}`);
  return data;
}
export async function fetchIpRules() {
  const { data } = await api.get("/admin/ip-rules");
  return data?.data || [];
}
export async function createIpRule(payload) {
  const { data } = await api.post("/admin/ip-rules", payload);
  return data?.data;
}
export async function deleteIpRule(id) {
  const { data } = await api.delete(`/admin/ip-rules/${id}`);
  return data;
}
export async function fetchSettings() {
  const { data } = await api.get("/admin/settings");
  return data?.data || [];
}
export async function patchSetting(key, value) {
  const { data } = await api.patch(`/admin/settings/${key}`, { value });
  return data?.data;
}

/* Phase A-3 — Users */
export async function fetchUsers(params = {}) {
  const qs = new URLSearchParams(params).toString();
  const { data } = await api.get(`/admin/users?${qs}`);
  return data;
}
export async function fetchUserDetail(id) {
  const { data } = await api.get(`/admin/users/${id}`);
  return data?.data;
}
export async function patchUser(id, payload) {
  const { data } = await api.patch(`/admin/users/${id}`, payload);
  return data;
}
export async function deleteUser(id) {
  const { data } = await api.delete(`/admin/users/${id}`);
  return data;
}
export async function createUser(payload) {
  const { data } = await api.post("/admin/users", payload);
  return data?.data;
}
export async function resetUserPassword(id) {
  const { data } = await api.post(`/admin/users/${id}/reset-password`);
  return data?.data;
}

/* Phase A-4 — Resources */
export async function fetchStorageStats() {
  const { data } = await api.get("/admin/storage/stats");
  return data?.data;
}
export async function fetchStorageFiles(params = {}) {
  const qs = new URLSearchParams(params).toString();
  const { data } = await api.get(`/admin/storage/files?${qs}`);
  return data;
}
export async function deleteStorageFile(id) {
  const { data } = await api.delete(`/admin/storage/files/${id}`);
  return data;
}
export async function fetchLlmStats() {
  const { data } = await api.get("/admin/llm/stats");
  return data?.data;
}
export async function fetchLlmLogs(params = {}) {
  const qs = new URLSearchParams(params).toString();
  const { data } = await api.get(`/admin/llm/logs?${qs}`);
  return data;
}
export async function fetchPerformance() {
  const { data } = await api.get("/admin/performance");
  return data?.data;
}


/* Phase A-5 — Analytics */
export async function fetchAnalyticsKpis() {
  const { data } = await api.get("/admin/analytics/kpis");
  return data?.data;
}
export async function fetchTraffic30d() {
  const { data } = await api.get("/admin/analytics/traffic");
  return data?.data || [];
}
export async function fetchTopPages() {
  const { data } = await api.get("/admin/analytics/pages");
  return data?.data || [];
}
export async function fetchFeatureUsage(days = 30) {
  const { data } = await api.get(`/admin/analytics/features?days=${days}`);
  return data?.data || [];
}
export async function fetchAnalyticsCountries() {
  const { data } = await api.get("/admin/analytics/countries");
  return data?.data || [];
}
export async function fetchDevices() {
  const { data } = await api.get("/admin/analytics/devices");
  return data?.data || [];
}
export async function fetchFunnel() {
  const { data } = await api.get("/admin/analytics/funnel");
  return data?.data || [];
}
export async function fetchErrors(params = {}) {
  const qs = new URLSearchParams(params).toString();
  const { data } = await api.get(`/admin/analytics/errors?${qs}`);
  return data;
}
export async function resolveErrorLog(id, resolved = true) {
  const { data } = await api.patch(`/admin/analytics/errors/${id}`, { resolved });
  return data;
}
export async function logClientError(payload) {
  const { data } = await api.post("/admin/analytics/error", payload);
  return data;
}
export async function pingPageView(payload) {
  try {
    await api.post("/admin/analytics/page-view", payload);
  } catch {
    /* best-effort */
  }
}
export async function pingFeature(feature_name, extra = {}) {
  try {
    await api.post("/admin/analytics/feature", { feature_name, ...extra });
  } catch {
    /* best-effort */
  }
}

/* Phase A-6 — Contacts / Newsletter / Notifications */
export async function fetchContactMessages(params = {}) {
  const qs = new URLSearchParams(params).toString();
  const { data } = await api.get(`/admin/contacts?${qs}`);
  return data;
}
export async function fetchContactDetail(id) {
  const { data } = await api.get(`/admin/contacts/${id}`);
  return data?.data;
}
export async function patchContact(id, payload) {
  const { data } = await api.patch(`/admin/contacts/${id}`, payload);
  return data;
}
export async function replyContact(id, payload) {
  const { data } = await api.post(`/admin/contacts/${id}/reply`, payload);
  return data;
}
export async function fetchBroadcasts() {
  const { data } = await api.get("/admin/notifications");
  return data?.data || [];
}
export async function sendBroadcast(payload) {
  const { data } = await api.post("/admin/notifications", payload);
  return data;
}
export async function fetchSubscribers(params = {}) {
  const qs = new URLSearchParams(params).toString();
  const { data } = await api.get(`/admin/newsletter?${qs}`);
  return data;
}
export async function toggleSubscriber(id, is_active) {
  const { data } = await api.patch(`/admin/newsletter/${id}`, { is_active });
  return data;
}
export async function deleteSubscriber(id) {
  const { data } = await api.delete(`/admin/newsletter/${id}`);
  return data;
}
export async function sendNewsletter(payload) {
  const { data } = await api.post("/admin/newsletter/send", payload);
  return data;
}
export function subscribersCsvUrl() {
  return `${api.defaults.baseURL || ""}/admin/newsletter/export.csv`;
}
export async function fetchMyNotifications() {
  const { data } = await api.get("/notifications");
  return data;
}
export async function markNotifRead(id) {
  const { data } = await api.patch(`/notifications/${id}/read`);
  return data;
}

/* Public (no auth) */
export async function submitContactForm(payload) {
  const { data } = await api.post("/contact", payload);
  return data;
}
export async function subscribeNewsletter(payload) {
  const { data } = await api.post("/newsletter/subscribe", payload);
  return data;
}

/* Phase A-7 — Content / Config */
export async function fetchContentList(section) {
  const qs = section ? `?section=${encodeURIComponent(section)}` : "";
  const { data } = await api.get(`/admin/content${qs}`);
  return data?.data || [];
}
export async function patchContent(key, payload) {
  const { data } = await api.patch(`/admin/content/${key}`, payload);
  return data;
}
export async function fetchQuotes() {
  const { data } = await api.get("/admin/quotes");
  return data?.data || [];
}
export async function addQuote(payload) {
  const { data } = await api.post("/admin/quotes", payload);
  return data?.data;
}
export async function patchQuote(id, payload) {
  const { data } = await api.patch(`/admin/quotes/${id}`, payload);
  return data;
}
export async function deleteQuote(id) {
  const { data } = await api.delete(`/admin/quotes/${id}`);
  return data;
}
export async function fetchCountries() {
  const { data } = await api.get("/admin/reference/countries");
  return data?.data || [];
}
export async function addCountry(payload) {
  const { data } = await api.post("/admin/reference/countries", payload);
  return data?.data;
}
export async function patchCountry(id, payload) {
  const { data } = await api.patch(`/admin/reference/countries/${id}`, payload);
  return data;
}
export async function fetchProfiles() {
  const { data } = await api.get("/admin/reference/profiles");
  return data?.data || [];
}
export async function addProfile(payload) {
  const { data } = await api.post("/admin/reference/profiles", payload);
  return data?.data;
}
export async function patchProfile(id, payload) {
  const { data } = await api.patch(`/admin/reference/profiles/${id}`, payload);
  return data;
}
export async function fetchSectors() {
  const { data } = await api.get("/admin/reference/sectors");
  return data;
}
export async function patchSector(id, payload) {
  const { data } = await api.patch(`/admin/reference/sectors/${id}`, payload);
  return data;
}
export async function rebalanceSectors() {
  const { data } = await api.post("/admin/reference/sectors/rebalance");
  return data;
}
export async function fetchFlags() {
  const { data } = await api.get("/admin/flags");
  return data?.data || [];
}
export async function patchFlag(key, payload) {
  const { data } = await api.patch(`/admin/flags/${key}`, payload);
  return data;
}
export async function fetchSmtpConfig() {
  const { data } = await api.get("/admin/smtp/config");
  return data?.data;
}
export async function patchSmtpConfig(payload) {
  const { data } = await api.patch("/admin/smtp/config", payload);
  return data;
}
export async function testSmtp(to) {
  const { data } = await api.post("/admin/smtp/test", { to });
  return data;
}
export async function fetchTemplates() {
  const { data } = await api.get("/admin/templates");
  return data?.data || [];
}
export async function patchTemplate(key, payload) {
  const { data } = await api.patch(`/admin/templates/${key}`, payload);
  return data;
}
export async function fetchAuditStats() {
  const { data } = await api.get("/admin/audit/stats");
  return data?.data;
}

/* Admin Profile & API settings (drop-in dashboard) */
export async function fetchProfileSettings() {
  const { data } = await api.get("/admin/profile/settings");
  return data?.data;
}
export async function saveCustomApiKey(apiKey) {
  const { data } = await api.put("/admin/profile/api-key", { api_key: apiKey });
  return data?.data;
}
export async function revertToUniversalKey() {
  const { data } = await api.delete("/admin/profile/api-key");
  return data?.data;
}
export async function testApiKey(apiKey) {
  const payload = apiKey ? { api_key: apiKey } : {};
  const { data } = await api.post("/admin/profile/api-key/test", payload);
  return data?.data;
}
export async function saveRecoveryEmail(email) {
  const { data } = await api.put("/admin/profile/recovery-email", { email });
  return data?.data;
}
export async function changeAdminPassword(currentPassword, newPassword) {
  const { data } = await api.put("/admin/profile/password", {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return data?.data;
}

